"use client";

import React from "react";
import { CircleCheck, Globe, Search, Smartphone, ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const expertiseData = [{
  title: "Wellness & Vaccinations",
  description: "Preventative care tailored to your pet's age and lifestyle, ensuring they stay protected against common diseases.",
  icon: Smartphone,
  features: ["Core & Lifestyle Vaccines", "Comprehensive Physical Exams", "Parasite Prevention Plans", "Nutritional Counseling"],
  link: "/ServicesPage",
  color: "text-primary",
  bgColor: "bg-primary/10"
}, {
  title: "Advanced Surgery",
  description: "State-of-the-art surgical suite for routine procedures and complex soft-tissue or orthopedic operations.",
  icon: Globe,
  features: ["Safe Anesthesia Monitoring", "Spay & Neuter Services", "Soft Tissue Procedures", "Post-Op Pain Management"],
  link: "/ServicesPage",
  color: "text-secondary",
  bgColor: "bg-secondary/10"
}, {
  title: "Diagnostic Imaging",
  description: "In-house digital radiology and ultrasound for rapid, accurate diagnosis of internal health concerns.",
  icon: Search,
  features: ["Digital X-Ray Technology", "Abdominal Ultrasound", "Cardiac Screenings", "Rapid Results Reporting"],
  link: "/ServicesPage",
  color: "text-primary",
  bgColor: "bg-primary/10"
}];
export default function ComprehensiveCare() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="235056" id="comprehensive-care" className="relative py-24 bg-background text-foreground overflow-hidden">
      {}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] rounded-lg bg-primary/5 blur-[100px]" />
        <div className="absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] rounded-lg bg-secondary/5 blur-[100px]" />
        <div className="absolute top-[40%] left-[60%] w-[20%] h-[20%] rounded-lg bg-primary/5 blur-[80px]" />
        
        {}
        <div className="absolute inset-0 opacity-[0.03]" style={{
        backgroundImage: 'radial-gradient(var(--color-foreground) 1px, transparent 1px)',
        backgroundSize: '32px 32px'
      }} />
      </div>

      <div className="container mx-auto px-4 md:px-6 relative z-10">
        <div ref={headerRef} className={`max-w-3xl mx-auto text-center mb-16 transition-all duration-700 ease-out ${isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <h2 className="font-default text-3xl md:text-5xl font-bold text-foreground mb-6 tracking-tight">
            Our Medical <span className="text-primary">Expertise</span>
          </h2>
          <p className="font-default text-lg text-muted-foreground">
            Providing compassionate, high-standard veterinary medicine with a focus on preventative health and advanced surgical care for your beloved companions.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {expertiseData.map((item, i) => <ExpertiseCard key={i} item={item} index={i} />)}
        </div>
      </div>
    </section>;
}
function ExpertiseCard({
  item,
  index
}: {
  item: any;
  index: number;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const delay = index * 150;
  return <div data-index={index} ref={ref} className={`transition-all duration-700 ease-out h-full ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`} style={{
    transitionDelay: `${delay}ms`
  }}>
      <Card className="h-full flex flex-col bg-card/60 backdrop-blur-xl border-border/50 shadow-md hover:shadow-lg hover:scale-[1.02] transition-all duration-300 group rounded-2xl">
        <CardHeader className="pb-4">
          {index > 2 && <div className={`w-14 h-14 rounded-lg ${item.bgColor} flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300`}>
              <item.icon className={`w-7 h-7 ${item.color}`} />
            </div>}
          <CardTitle className="font-default text-2xl text-foreground mb-2">
            {item.title}
          </CardTitle>
          <CardDescription className="font-default text-base text-muted-foreground leading-relaxed">
            {item.description}
          </CardDescription>
        </CardHeader>
        
        <CardContent className="flex-grow">
          <ul className="space-y-3 mt-2">
            {item.features.map((feature: string, fIndex: number) => <li key={fIndex} data-index={fIndex} className="flex items-start gap-3">
                <CircleCheck className="w-5 h-5 text-secondary shrink-0 mt-0.5" />
                <span className="font-default text-sm text-foreground/80">{feature}</span>
              </li>)}
          </ul>
        </CardContent>
        
        <CardFooter className="pt-6 border-t border-border/30 mt-auto">
          <Button asChild variant="ghost" className="w-full justify-between group/btn hover:bg-primary/5 hover:text-primary rounded-md">
            <Link to={item.link}>
              Learn More
              <ArrowRight className="w-4 h-4 ml-2 group-hover/btn:translate-x-1 transition-transform" />
            </Link>
          </Button>
        </CardFooter>
      </Card>
    </div>;
}